<script lang="ts" setup>
import { Back, Delete, Loading, Plus, Upload } from '@element-plus/icons-vue'
import { ImageIcon } from '@metapic-apps/shared'
import type { FormInstance, FormRules } from 'element-plus'
import { ElButton, ElMessage, ElNotification } from 'element-plus'
import type { UploadFile } from 'element-plus/es/components/upload/src/upload'
import { posthog } from 'posthog-js'
import { computed, reactive, ref, watch, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import HelpIcon from '@/assets/icons/help.svg?component'
import LogoSvg from '@/assets/icons/metapic-logo-black.svg?component'
import { regexValidationFunc, validVatMap, vatFormats } from '@/legacy/externals/vat'
import type { AdvertiserRepresentativeFormData as FormData } from '@/lib'
import { fromFormToStoreCreation } from '@/lib/adapters/store.adapters'
import { CATEGORIES } from '@/lib/constants/categories'
import { COUNTRIES_DATA, EU_COUNTRIES } from '@/lib/constants/countries'
import { config, formatMoneyInLocale } from '@/lib/helpers'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import { useAuthStore } from '@/stores/auth.stores'
import { useUserStore } from '@/stores/user.stores'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

const store = useStore()
const route = useRoute()
const router = useRouter()
const MAXIMUM_UPLOAD_SIZE = 10 // mb
const description = {
  0: 'Let’s get started. Which of these categories best describes your business?',
  1: 'Tell us about your business.',
  2: 'What is your billing address?',
  3: 'Add payment information.',
}
const progress = {
  0: 5,
  1: 25,
  2: 50,
  3: 95,
}
const vatLabel = computed(() => {
  switch (formData.locale) {
    case 'NO':
      return 'MVA number'
    case 'NL':
      return 'BTW number'
    case 'AU':
      return 'ABN number'
    case 'ES':
      return 'NIF number'
    default:
      return 'VAT number'
  }
})
const paymentIntentClientSecret = route.query.payment_intent_client_secret
const step = ref<keyof typeof description>(0)
const isServerVatValidationFailed = ref(false)

const ruleFormRef = ref<FormInstance>()
const logo = ref()
const isLocalLoading = ref(false)
const advertiserStore = useAdvertiserStore()
const advertiser = computed(() => advertiserStore.advertiser)
const userStore = useUserStore()
const authStore = useAuthStore()
const isStripeLoaded = computed(() => store.getters['payment/elements'])
const minimumFee = computed(() => store.getters['payment/minimumFee'])
const isLoading = computed(() => isLocalLoading.value || store.getters['payment/isLoading'])
const formData = reactive<FormData>({
  categories: [],
  storeName: '',
  logo: '',
  domains: [
    {
      key: 1,
      value: '',
    },
  ],
  companyName: '',
  address: '',
  postalCode: '',
  city: '',
  locale: 'DE',
  vatNumber: '',
})
const selectedFlag = computed(() => {
  const data = COUNTRIES_DATA.find((item) => item.iso === formData.locale)
  return data?.flag
})
const rules = reactive<FormRules<FormData>>({
  categories: [{ type: 'array', required: true, message: 'Please select at least one category', trigger: 'blur' }],
  storeName: [
    { required: true, message: 'This field is required', trigger: 'blur' },
    { max: 1000, message: 'Field must be shorter than 1000 symbols', trigger: 'blur' },
  ],
  logo: [{ required: true, message: 'This field is required', trigger: 'blur' }],
  domains: [
    {
      validator: (_, value: { key: string; value: string }[], callback) => {
        if (value.some((item) => !!item.value)) {
          callback()
        } else {
          callback(new Error('This field is required'))
        }
      },
      trigger: 'blur',
    },
  ],
  companyName: [
    { required: true, message: 'This field is required', trigger: 'blur' },
    { max: 1000, message: 'Field must be shorter than 1000 symbols', trigger: 'blur' },
  ],
  address: [
    { required: true, message: 'This field is required', trigger: 'blur' },
    { max: 1000, message: 'Field must be shorter than 1000 symbols', trigger: 'blur' },
  ],
  postalCode: [
    { required: true, message: 'This field is required', trigger: 'blur' },
    { max: 1000, message: 'Field must be shorter than 1000 symbols', trigger: 'blur' },
  ],
  city: [
    { required: true, message: 'This field is required', trigger: 'blur' },
    { max: 1000, message: 'Field must be shorter than 1000 symbols', trigger: 'blur' },
  ],
  vatNumber: [
    { max: 1000, message: 'Field must be shorter than 1000 symbols', trigger: 'blur' },
    {
      trigger: 'blur',
      validator: (_, value, callback) => {
        if (isServerVatValidationFailed.value) {
          return callback(new Error('The VAT number is invalid'))
        }

        if (!EU_COUNTRIES.includes(formData.locale)) {
          return callback()
        }

        if (!value) {
          return callback(new Error('This field is required'))
        }

        if (!regexValidationFunc(validVatMap[formData.locale as keyof typeof validVatMap], value)) {
          return callback(new Error('The VAT number is invalid'))
        }

        return callback()
      },
    },
  ],
})
const handleError = (e: Error) => {
  ElNotification.error({
    message: 'message' in e ? e.message : 'Oops! Something went wrong.',
    duration: 0,
  })
}

watchEffect(() => {
  if (advertiser.value?.stripeSubscriptionStatus === 'not_setup') {
    step.value = 3
    if (!isStripeLoaded.value) {
      void store.dispatch('payment/setupStripe', { storeId: advertiser.value.id, authorization: authStore.authorization })
    }
  }
})

watchEffect(() => {
  if (advertiser.value && advertiser.value?.stripeSubscriptionStatus !== 'not_setup' && userStore.user?.advertisers.length !== 0) {
    void router.replace('/')
  }
})

watchEffect(() => {
  if (config.isPosthogEnabled) {
    posthog.opt_in_capturing()
  }
})

watch(
  () => formData.vatNumber,
  (newValue) => {
    formData.vatNumber = newValue.replace(/\s+/g, '')
  },
)

const addDomain = () => {
  formData.domains.push({ key: Date.now(), value: '' })
}

const removeDomain = (item: FormData['domains'][number]) => {
  const index = formData.domains.indexOf(item)
  if (index !== -1) {
    formData.domains.splice(index, 1)
  }
}

const handleLogoSuccess = (url: string, uploadFile: UploadFile) => {
  formData.logo = url
  if (uploadFile.raw) {
    logo.value = URL.createObjectURL(uploadFile.raw)
  }
  isLocalLoading.value = false
}

const handleLogoError = (e: Error) => {
  handleError(e)
  isLocalLoading.value = false
}

const beforeLogoUpload = (rawFile: File) => {
  if (!['image/jpeg', 'image/png'].includes(rawFile.type)) {
    ElMessage.error('The logo must be in JPG or PNG format')
    return false
  } else if (rawFile.size / 1024 / 1024 > MAXIMUM_UPLOAD_SIZE) {
    ElMessage.error(`The size of the logo can not exceed ${MAXIMUM_UPLOAD_SIZE}mb`)
    return false
  }
  isLocalLoading.value = true
  return true
}

const removeThumbnailLogo = (e: MouseEvent) => {
  e.stopPropagation()
  logo.value = null
  formData.logo = undefined
}

const handleBack = () => {
  step.value--
}

const createAdvertiserRepresentative = async (formData: FormData) => {
  try {
    isLocalLoading.value = true
    const { error } = await store.dispatch('shop/createAdvertiserRepresentative', fromFormToStoreCreation(formData))
    if (error) {
      return { error }
    }

    await authStore.authenticate()
    await userStore.initialize()

    const advertiserId = userStore.user?.advertisers[0]?.id
    return advertiserId ? { advertiserId } : { error: new Error('Failed to retrieve advertiser ID') }
  } catch (e) {
    return { error: e as Error }
  } finally {
    isLocalLoading.value = false
  }
}

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) {
    return
  }
  try {
    switch (step.value) {
      case 0: {
        await formEl.validateField(['categories'], (valid) => {
          if (valid) {
            step.value = 1
          }
        })
        break
      }
      case 1: {
        const domainsFields = formData.domains.map((_, index) => `domains.${index}.value`)
        await formEl.validateField(['domains', 'logo', 'storeName', ...domainsFields], (valid) => {
          if (valid) {
            step.value = 2
          }
        })
        break
      }
      case 2: {
        isServerVatValidationFailed.value = false
        await formEl.validateField(['companyName', 'address', 'postalCode', 'city', 'locale', 'vatNumber'], async (valid) => {
          if (valid) {
            isLocalLoading.value = true
            const { advertiserId, error } = await createAdvertiserRepresentative(formData)
            isLocalLoading.value = false
            if (error?.errors?.['billing.vat_number']) {
              isServerVatValidationFailed.value = true
              await formEl.validateField(['vatNumber'])
            } else if (error) {
              handleError(error)
            }

            if (advertiserId) {
              step.value = 3
              if (!isStripeLoaded.value && userStore.user) {
                await store.dispatch('payment/setupStripe', {
                  storeId: advertiserId,
                  authorization: authStore.authorization,
                })
              }
            }
          }
        })
        break
      }
      case 3: {
        await store.dispatch('payment/sendPayment')
        break
      }
    }
  } catch (e) {
    handleError(e as Error)
  }
}
</script>
<template>
  <FlexComponent v-if="advertiserStore.isLoading" flex="1" justify="center" class="loading-spinner--container">
    <img width="75px" src="../assets/loading.svg" alt="loading" class="loading-spinner" />
  </FlexComponent>
  <FlexComponent v-else class="signup-container">
    <FlexComponent align="center" shrink="0" class="signup-image-wrapper">
      <img src="https://static.metapic.com/app_assets/onboarding-side-image.png" alt="register-img" class="signup-image" />
    </FlexComponent>
    <FlexComponent class="signup-form-wrapper">
      <FlexComponent direction="column" justify="center" grow="0" class="signup-form-container">
        <FlexComponent justify="space-between" align="center">
          <LogoSvg :width="190" />
          <router-link to="/logout" class="logout-link">
            <TypographyComponent variant="label-large" color="#000000">Logout</TypographyComponent>
          </router-link>
        </FlexComponent>
        <template v-if="paymentIntentClientSecret">
          <FlexComponent flex="1" justify="center" style="height: 150px">
            <img width="75px" src="../assets/loading.svg" alt="loading" class="loading-spinner" />
          </FlexComponent>
        </template>
        <template v-else>
          <BoxComponent :mt="48">
            <el-progress :stroke-width="8" :percentage="progress[step]" color="#000000" :show-text="false" />
          </BoxComponent>
          <BoxComponent :mt="48" :mb="48">
            <TypographyComponent variant="headline-medium">
              {{ description[step] }}
            </TypographyComponent>
          </BoxComponent>
          <el-form
            ref="ruleFormRef"
            :model="formData"
            :rules="rules"
            hide-required-asterisk
            :validate-on-rule-change="false"
            :scroll-to-error="true"
            label-position="top"
            @submit.prevent="submitForm(ruleFormRef)"
          >
            <BoxComponent :mb="48">
              <el-form-item v-if="step === 0" prop="categories">
                <el-checkbox-group v-model="formData.categories">
                  <BoxComponent :mb="16">
                    <FlexComponent :gap="16" wrap="wrap">
                      <el-checkbox-button v-for="categoryItem in CATEGORIES" :key="categoryItem.id" :label="categoryItem.id" border>
                        <el-icon :size="24">
                          <component :is="categoryItem.icon" />
                        </el-icon>
                        <TypographyComponent is="span" text-align="left" variant="label-large">
                          {{ categoryItem.name }}
                        </TypographyComponent>
                      </el-checkbox-button>
                    </FlexComponent>
                  </BoxComponent>
                </el-checkbox-group>
              </el-form-item>
              <FlexComponent v-else-if="step === 1" :gap="24" direction="column">
                <div>
                  <TypographyComponent :is="BoxComponent" :mb="8" variant="label-large">
                    <FlexComponent align="center" :gap="8">
                      <label for="name">Your business name</label>
                      <el-tooltip class="box-item" effect="light" placement="top">
                        <template #content>
                          <div class="tooltip-content">
                            <TypographyComponent variant="title-medium">Business name</TypographyComponent>
                            <TypographyComponent variant="body-medium">
                              This name will be shown to creators. We recommend using your informal company name.
                            </TypographyComponent>
                            <TypographyComponent variant="body-medium">
                              E.g. we as “Metapic Sweden AB” would use “Metapic”.
                            </TypographyComponent>
                          </div>
                        </template>
                        <el-icon :size="18" color="red">
                          <HelpIcon />
                        </el-icon>
                      </el-tooltip>
                    </FlexComponent>
                  </TypographyComponent>
                  <el-form-item prop="storeName">
                    <el-input v-model="formData.storeName" size="large" />
                  </el-form-item>
                </div>
                <div>
                  <BoxComponent :mb="8">
                    <TypographyComponent is="label" variant="label-large">Your logo</TypographyComponent>
                  </BoxComponent>
                  <el-form-item prop="logo">
                    <el-upload
                      class="w-100"
                      drag
                      :action="`${config.metapicApiUrl}/advertiser/stores/upload-logo-temp`"
                      name="logo"
                      :show-file-list="false"
                      :on-success="handleLogoSuccess"
                      :on-error="handleLogoError"
                      :before-upload="beforeLogoUpload"
                    >
                      <FlexComponent :gap="16" basis="100%">
                        <FlexComponent align="center" justify="center" shrink="0" class="upload-logo-container">
                          <template v-if="logo">
                            <img :src="logo" class="upload-logo-thumbnail" alt="store logo" />
                            <span class="thumbnail-actions" @click.prevent="removeThumbnailLogo">
                              <el-icon :size="24" class="cursor-pointer">
                                <Delete />
                              </el-icon>
                            </span>
                          </template>
                          <el-icon v-else :class="{ 'is-loading': isLoading }" :size="24">
                            <Loading v-if="isLoading" />
                            <ImageIcon v-else />
                          </el-icon>
                        </FlexComponent>
                        <FlexComponent flex="1" direction="column" justify="center" basis="100%">
                          <div class="el-upload__text">
                            <TypographyComponent variant="body-small" color="rgba(3, 30, 81, 0.64)">
                              Must be JPG or PNG, maximum file size 10MB.
                            </TypographyComponent>
                          </div>
                          <div>
                            <ButtonComponent variant="accent" class="upload-button">
                              <template #iconLeft>
                                <el-icon :size="18">
                                  <Upload />
                                </el-icon>
                              </template>
                              Browse
                            </ButtonComponent>
                          </div>
                        </FlexComponent>
                      </FlexComponent>
                    </el-upload>
                  </el-form-item>
                </div>
                <div>
                  <BoxComponent :mb="8">
                    <TypographyComponent is="label" variant="label-large">Website</TypographyComponent>
                  </BoxComponent>
                  <BoxComponent :mb="8">
                    <el-form-item prop="domains">
                      <FlexComponent :gap="24" direction="column" basis="100%">
                        <el-form-item
                          v-for="(domain, index) in formData.domains"
                          :key="domain.key"
                          :prop="`domains.${index}.value`"
                          :rules="{
                            type: 'string',
                            pattern: '^(https?:\\/\\/)?([\\dA-Za-z\\.-]+)\\.([A-Za-z\\.]{2,6})([\\/\\w \\.-]*)*\\/?$',
                            message: 'Please input valid URL',
                            trigger: 'blur',
                          }"
                        >
                          <FlexComponent :gap="8" class="w-100">
                            <el-input v-model="domain.value" placeholder="https://" size="large" />
                            <ElButton v-if="formData.domains.length > 1" size="large" text @click="removeDomain(domain)">
                              <el-icon :size="24">
                                <Delete />
                              </el-icon>
                            </ElButton>
                          </FlexComponent>
                        </el-form-item>
                      </FlexComponent>
                    </el-form-item>
                  </BoxComponent>
                  <FlexComponent v-if="formData.domains.length < 10" justify="flex-end">
                    <TypographyComponent :is="ElButton" plain :icon="Plus" variant="label-medium" @click="addDomain">
                      Add more websites
                    </TypographyComponent>
                  </FlexComponent>
                </div>
              </FlexComponent>
              <FlexComponent v-else-if="step === 2" :gap="24" direction="column">
                <div>
                  <BoxComponent :mb="8">
                    <TypographyComponent is="label" variant="label-large">Company name</TypographyComponent>
                  </BoxComponent>
                  <el-form-item prop="companyName">
                    <el-input v-model="formData.companyName" size="large" />
                  </el-form-item>
                </div>
                <div>
                  <BoxComponent :mb="8">
                    <TypographyComponent is="label" variant="label-large">Address</TypographyComponent>
                  </BoxComponent>
                  <el-form-item prop="address">
                    <el-input v-model="formData.address" size="large" />
                  </el-form-item>
                </div>
                <FlexComponent :gap="16">
                  <FlexComponent direction="column" basis="50%">
                    <BoxComponent :mb="8">
                      <TypographyComponent is="label" variant="label-large">Postal code</TypographyComponent>
                    </BoxComponent>
                    <el-form-item prop="postalCode">
                      <el-input v-model="formData.postalCode" size="large" />
                    </el-form-item>
                  </FlexComponent>
                  <FlexComponent direction="column" basis="100%">
                    <BoxComponent :mb="8">
                      <TypographyComponent is="label" variant="label-large">City</TypographyComponent>
                    </BoxComponent>
                    <el-form-item prop="city">
                      <el-input v-model="formData.city" size="large" />
                    </el-form-item>
                  </FlexComponent>
                </FlexComponent>
                <div>
                  <BoxComponent :mb="8">
                    <TypographyComponent is="label" variant="label-large">Country</TypographyComponent>
                  </BoxComponent>
                  <el-form-item prop="locale">
                    <el-select v-model="formData.locale" size="large">
                      <template #prefix>
                        <el-icon :size="20">
                          <component :is="selectedFlag" />
                        </el-icon>
                      </template>
                      <el-option v-for="item in COUNTRIES_DATA" :key="item.iso" :label="item.name" :value="item.iso">
                        <FlexComponent :gap="16" align="center" style="height: 100%">
                          <el-icon :size="20">
                            <component :is="item.flag" />
                          </el-icon>
                          <TypographyComponent is="span" variant="label-medium">{{ item.name }}</TypographyComponent>
                        </FlexComponent>
                      </el-option>
                    </el-select>
                  </el-form-item>
                </div>
                <div>
                  <BoxComponent :mb="8">
                    <TypographyComponent is="label" variant="label-large">{{ vatLabel }}</TypographyComponent>
                  </BoxComponent>
                  <el-form-item prop="vatNumber">
                    <el-input
                      v-model="formData.vatNumber"
                      size="large"
                      :placeholder="`E.g. ${vatFormats[formData.locale as keyof typeof vatFormats]}`"
                      :disabled="isLoading"
                    />
                  </el-form-item>
                </div>
              </FlexComponent>
              <FlexComponent v-else-if="step === 3" :gap="48" direction="column">
                <FlexComponent v-if="!isStripeLoaded" flex="1" justify="center" style="height: 150px">
                  <img width="75px" src="../assets/loading.svg" alt="loading" class="loading-spinner" />
                </FlexComponent>
                <FlexComponent v-else class="payment-description" align="center" direction="column">
                  <TypographyComponent variant="title-medium"> Monthly subscription fee: </TypographyComponent>
                  <TypographyComponent variant="body-medium" text-align="center">10% of creator spending.</TypographyComponent>
                  <TypographyComponent variant="body-medium" text-align="center"
                    >No minimum in the first month; {{ formatMoneyInLocale(minimumFee, formData.locale) }} minimum applies from month two
                    onward.</TypographyComponent
                  >
                </FlexComponent>
                <div id="payment-element"></div>
              </FlexComponent>
            </BoxComponent>
            <FlexComponent justify="space-between">
              <div>
                <ElButton v-if="step === 1 || step === 2" size="large" round @click="handleBack">
                  <el-icon :size="24">
                    <Back />
                  </el-icon>
                </ElButton>
              </div>
              <ButtonComponent variant="accent" :is-loading="isLoading" type="submit"> Continue </ButtonComponent>
            </FlexComponent>
          </el-form>
        </template>
      </FlexComponent>
    </FlexComponent>
  </FlexComponent>
</template>
<style lang="scss" scoped>
$breakpoint-md: 768px;
$breakpoint-lg: 992px;
:deep(.el-checkbox-button) {
  --el-border-color: rgba(0, 0, 0, 0.24);
  --el-border-radius-base: 8px;
  width: calc(50% - 8px);

  &.is-checked {
    path {
      fill: white;
    }

    span {
      color: white;
    }
  }
}

:deep(.el-checkbox-button__inner) {
  width: 100%;
  height: 58px;
  display: flex;
  align-items: center;
  gap: 16px;
  border-radius: var(--el-border-radius-base);
  padding: 8px 16px;
  border: 1px solid var(--el-border-color);
  white-space: normal;
}

:deep(input[type='text']),
:deep(input[type='password']) {
  height: initial;
}

:deep(.el-upload-dragger) {
  --el-color-primary: var(--el-border-color);
  border: 1px solid var(--el-border-color);

  &:hover {
    box-shadow: 0 0 0 1px var(--el-border-color-hover) inset;
  }
}

:deep(.el-form-item) {
  margin-bottom: 0;
}

.signup-container {
  height: 100%;
  width: 100vw;
  @media (min-width: $breakpoint-lg) {
    height: 100vh;
  }
}

.signup-image-wrapper {
  display: none;
  background: var(--primary);

  @media (min-width: $breakpoint-lg) {
    display: block;
    position: relative;
    height: 100%;
    width: 480px;
  }
}

.signup-image {
  position: absolute;
  width: 600px;
  bottom: calc(50% - 210px);
}

.signup-form-wrapper {
  overflow: auto;
  height: 100%;
  width: 100%;
  padding-bottom: 24px;

  @media (min-width: $breakpoint-lg) {
    padding-left: 80px;
    align-items: center;
  }
}

.signup-form-container {
  width: min(calc(100% - 48px), 440px);
  margin: 24px auto 0;
  justify-content: flex-start;

  @media (min-width: $breakpoint-lg) {
    justify-content: center;
  }
}

.upload-logo-container {
  position: relative;
  width: 84px;
  height: 84px;
  border-radius: 8px;
  border: 1px dashed rgba(3, 30, 81, 0.24);

  &:hover > img + .thumbnail-actions {
    opacity: 1;
  }
}

.upload-logo-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.thumbnail-actions {
  background: rgba(0, 0, 0, 0.5);
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  color: #fff;
  z-index: 2;
  opacity: 0;
  cursor: pointer;
}

.payment-description {
  border-radius: 8px;
  padding: 16px;
  background: var(--pale-lavender-500);
}

.tooltip-content {
  width: 310px;
}

.logout-link {
  text-decoration: none;

  @media (min-width: $breakpoint-lg) {
    position: absolute;
    top: 24px;
    right: 24px;
  }
}
</style>
